import React, { useEffect, useState } from "react";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";
import toast, { Toaster } from "react-hot-toast";
import resolveImageUrl from "../../utils/imageUtils";

const UploadOldProperty = () => {
  const User = JSON.parse(localStorage.getItem("user"));
  const userId = User.id;

  const initialFeatures = [
    { name: "Floor", before: "", after: "" },
    { name: "Land Use", before: "", after: "" },
    { name: "Land Area", before: "", after: "" },
    { name: "Covered Area", before: "", after: "" },
  ];

  const [pins, setPins] = useState([]);
  const [selectedPin, setSelectedPin] = useState("");
  const [filteredPins, setFilteredPins] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const [circle, setCircle] = useState("");
  const [occupationStatus, setOccupationStatus] = useState("");
  const [location, setLocation] = useState("");
  const [features, setFeatures] = useState(initialFeatures);
  const [preview, setPreview] = useState(null);
  const [allowEdit, setAllowEdit] = useState(false);

  // fetch all available pins
  useEffect(() => {
    const fetchPins = async () => {
      try {
        const { data } = await axios.get("/inspector/pins");
        setPins(data);
      } catch (err) {
        console.error("Error fetching pins", err);
        toast.error("Failed to load pins.");
      }
    };
    fetchPins();
  }, []);

  // fetch property data by pin
  const fetchProperty = async (pin) => {
    try {
      const { data } = await axios.get(`/inspector/property/${pin}`);
      setCircle(data.circle || "");
      setOccupationStatus(data.occupationstatus || "");
      setLocation(data.location || "");
      setFeatures([
        { name: "Floor", before: data.floor_admin || "", after: data.floors_pred || "" },
        { name: "Land Use", before: data.landuse_admin || "", after: data.land_use_pred || "" },
        { name: "Land Area", before: data.landarea_admin || "", after: data.landarea_combined_pred || "" },
        { name: "Covered Area", before: data.coveredarea_admin || "", after: data.coveredarea_combined_pred || "" },
      ]);
      setPreview(resolveImageUrl(data.image));
      setAllowEdit(false);
    } catch (err) {
      console.error("Error fetching property", err);
      toast.error("Failed to load property details.");
    }
  };

  const handleFeatureChange = (index, field, value) => {
    const updated = [...features];
    updated[index][field] = value;
    setFeatures(updated);
  };

  const handleSubmit = async () => {
    if (!selectedPin || !pins.includes(selectedPin)) {
      toast.error("Please select a valid PIN!");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("circle", circle);
      formData.append("occupationstatus", occupationStatus);
      formData.append("location", location);
      formData.append("floor_admin", features[0].before);
      formData.append("landuse_admin", features[1].before);
      formData.append("landarea_admin", features[2].before);
      formData.append("coveredarea_admin", features[3].before);
      formData.append("floors_pred", features[0].after);
      formData.append("land_use_pred", features[1].after);
      formData.append("landarea_combined_pred", features[2].after);
      formData.append("coveredarea_combined_pred", features[3].after);
      formData.append("userId", userId);

      await axios.put(`/properties/${selectedPin}`, formData);
      toast.success("Property updated successfully!");
    } catch (error) {
      console.error("Error updating property:", error);
      toast.error("Failed to update property.");
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 p-8 overflow-y-auto">
        <Toaster position="top-right" reverseOrder={false} />

        <h1 className="text-3xl font-bold text-gray-800 mb-8 border-b pb-4">
          Inspector Dashboard <span className="text-blue-600">– Upload Old Property</span>
        </h1>

        <div className="space-y-8">
          {/* 🔍 Searchable PIN Selection */}
          <div className="bg-white rounded-xl shadow p-6 relative">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Search Property PIN</h2>

            <input
              type="text"
              value={selectedPin}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedPin(value);
                if (value.trim()) {
                  const filtered = pins.filter((p) =>
                    p.toLowerCase().includes(value.toLowerCase())
                  );
                  setFilteredPins(filtered);
                  setShowDropdown(true);
                } else {
                  setShowDropdown(false);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowDropdown(false), 150);
                if (!pins.includes(selectedPin)) setSelectedPin("");
              }}
              placeholder="Type to search property PIN..."
              className="w-full p-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            {/* Dropdown List */}
            {showDropdown && filteredPins.length > 0 && (
              <ul className="absolute z-10 bg-white border border-gray-300 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg w-full">
                {filteredPins.map((p, i) => (
                  <li
                    key={i}
                    onMouseDown={() => {
                      setSelectedPin(p);
                      setShowDropdown(false);
                      fetchProperty(p);
                    }}
                    className="px-4 py-2 cursor-pointer hover:bg-blue-100"
                  >
                    {p}
                  </li>
                ))}
              </ul>
            )}

            {/* No match found */}
            {showDropdown && filteredPins.length === 0 && (
              <ul className="absolute z-10 bg-white border border-gray-300 rounded-lg mt-1 shadow-lg w-full">
                <li className="px-4 py-2 text-gray-500">No matching PIN found</li>
              </ul>
            )}
          </div>

          {/* Property preview */}
          {selectedPin && pins.includes(selectedPin) && (
            <>
              <div className="bg-white rounded-xl shadow p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Property Preview</h2>
                {preview && (
                  <div className="mb-4">
                    <img src={preview} alt="Property" className="h-48 w-auto rounded-lg shadow border" />
                  </div>
                )}
                <p className="text-gray-700"><strong>Circle:</strong> {circle}</p>
                <p className="text-gray-700"><strong>Occupation Status:</strong> {occupationStatus}</p>
                <p className="text-gray-700"><strong>Address:</strong> {location}</p>
              </div>

              {/* Confirmation */}
              <div className="bg-white rounded-xl shadow p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Do you want to alter this property?</h2>
                <div className="flex gap-4">
                  <button
                    onClick={() => setAllowEdit(true)}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setAllowEdit(false)}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg shadow hover:bg-red-600 transition"
                  >
                    No
                  </button>
                </div>
              </div>

              {/* Editable Details */}
              {allowEdit && (
                <>
                  <div className="bg-white rounded-xl shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Edit Property Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-gray-600 font-medium mb-2">Circle</label>
                        <input
                          type="text"
                          value={circle}
                          onChange={(e) => setCircle(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-600 font-medium mb-2">Occupation Status</label>
                        <input
                          type="text"
                          value={occupationStatus}
                          onChange={(e) => setOccupationStatus(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-600 font-medium mb-2">Address</label>
                        <input
                          type="text"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Features Comparison */}
                  <div className="bg-white rounded-xl shadow p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Features Comparison</h2>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 border rounded-lg overflow-hidden">
                        <thead className="bg-blue-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Feature</th>
                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Before (Admin)</th>
                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">After (Predicted)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {features.map((feature, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium text-gray-700">{feature.name}</td>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={feature.before}
                                  onChange={(e) => handleFeatureChange(index, "before", e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded-lg"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={feature.after}
                                  onChange={(e) => handleFeatureChange(index, "after", e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded-lg"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleSubmit}
                      className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 transition"
                    >
                      Update Property
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadOldProperty;
